/**
 * g.fetch - talk to the app's API, to our own PHP, or to an external URL.
 *
 *   const res = await g.fetch(endpoint, data, opts);
 *   // res = the API's JSON envelope: { success, error?, data?, pagination? }
 *   // - never throws; when the request itself fails, g.fetch synthesizes the
 *   // same envelope shape (see CLIENT ERROR CODES below)
 *
 * Endpoint forms:
 *   '/path/to/endpoint'         -> settings.api + path, X-Api-Token header sent
 *   'native:/path/to/endpoint'  -> current origin + path (PHP wrappers - e.g.
 *                                  login/logout, which must set PHP cookies);
 *                                  no X-Api-Token
 *   'https://...' (full URL)    -> external API as-is; no X-Api-Token
 *
 * Cookies are ALWAYS sent (credentials: 'include') - the PHP session cookies
 * are the user's identity. Requires the API to answer CORS with the exact
 * origin (not *), Access-Control-Allow-Credentials: true and X-Api-Token in
 * Access-Control-Allow-Headers.
 *
 * The body always has the shape { data, pagination } so the API reads
 * req.body.data / req.body.pagination:
 *   - plain data                -> JSON request
 *   - data containing File/Blob/FileList (any depth) -> multipart FormData:
 *     files are appended under their key path (e.g. 'avatar', 'docs.0'),
 *     'data' and 'pagination' become JSON-string fields (the server shim
 *     JSON.parses them - see docs/libs/genus.md)
 *   - data instanceof FormData  -> sent as-is (escape hatch); only
 *     opts.pagination is appended, nothing else is touched
 *
 * opts.signal: an AbortSignal - aborting cancels the request (error.code 4).
 * opts.progress: false - keep this call off the top progress bar (chatty
 * background loads: selekt lists, validator server checks).
 *
 * ERROR CODES: error.code 0 means "no error". Codes generated HERE (client
 * side, reserved - the API's own codes come in the response body):
 *   1 = invalid endpoint (nothing was sent)
 *   2 = API not responding (network/CORS failure)
 *   3 = invalid API response (body is not valid JSON)
 *   4 = request aborted (opts.signal fired - the caller cancelled it)
 * The API can also answer success: true WITH error.code > 0 - an expected
 * business error (e.g. row not found). success: false = system error.
 *
 * Full reference: docs/libs/genus.md.
 */
import { settings } from './core.js';
import { route } from './route.js';
import { translate } from './translate.js';
import { progressStart, progressEnd } from './progress.js';

const isPlainObject = (v) => v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Blob) && !(v instanceof FormData);

const failure = (code, message) => ({ success: false, error: { code, message } });

// Where does this endpoint go, and does it get the API token?
function resolve(endpoint) {
	if (endpoint.startsWith('native:')) {
		// native = our own PHP: carry the page's context prefix (and language)
		// so the per-tab tenant reaches the PHP side too (tenancy.md) — the
		// router strips both before matching, every route answers either way
		let path = endpoint.slice('native:'.length);
		try { path = route.url(path); } catch { /* no routing payload on this page — send as-is */ }
		return { url: window.location.origin + path, withToken: false, tenant: false };
	}
	if (/^https?:\/\//.test(endpoint)) return { url: endpoint, withToken: false, tenant: false };
	if (endpoint.startsWith('/')) return { url: String(settings.api || '').replace(/\/+$/, '') + endpoint, withToken: true, tenant: true };
	return null;
}

// Deep-copy `value` with every File/Blob/FileList pulled out into `files`
// (as { key: 'path.to.field', file }). Removed spots become absent (objects)
// or null (arrays, to keep indices stable).
function stripFiles(value, path, files) {
	if (typeof FileList !== 'undefined' && value instanceof FileList) value = Array.from(value);
	if (value instanceof Blob) {
		files.push({ key: path || 'file', file: value });
		return undefined;
	}
	if (Array.isArray(value)) {
		return value.map((v, i) => {
			const r = stripFiles(v, `${path}.${i}`, files);
			return r === undefined ? null : r;
		});
	}
	if (isPlainObject(value)) {
		const copy = {};
		for (const k of Object.keys(value)) {
			const r = stripFiles(value[k], path ? `${path}.${k}` : k, files);
			if (r !== undefined) copy[k] = r;
		}
		return copy;
	}
	return value;
}

export async function apiFetch(endpoint, data, opts = {}) {
	if (opts.progress === false) return doFetch(endpoint, data, opts);
	progressStart();
	try {
		return await doFetch(endpoint, data, opts);
	} finally {
		progressEnd();
	}
}

async function doFetch(endpoint, data, opts) {
	const target = resolve(String(endpoint || ''));
	if (!target) return failure(1, translate('Invalid endpoint: {endpoint}', { endpoint }));

	const method = (opts.method || 'POST').toUpperCase();
	const headers = {};
	if (target.withToken && settings.apiToken) headers['X-Api-Token'] = settings.apiToken;
	if (target.withToken) {
		// the page's language rides every API call (i18n.md): business
		// errors / validation messages come back in the user's language
		const lang = (window.g && window.g.lang) || settings.lang;
		if (lang) headers['X-Genus-Lang'] = lang;
	}
	if (target.tenant) {
		// the page's ACTIVE tenant rides every API call automatically — lids
		// from the inlined routing payload (opaque handles; the API decodes
		// and validates them against the caller's memberships — tenancy.md)
		const company = route.context('company');
		const space = route.context('space');
		if (company) headers['X-Genus-Company'] = company;
		if (space) headers['X-Genus-Space'] = space;
	}

	let body;
	if (method === 'GET' || method === 'HEAD') {
		// These can't carry a body - data/pagination are ignored (use POST).
		body = undefined;
	} else if (data instanceof FormData) {
		body = data;
		if (opts.pagination) body.append('pagination', JSON.stringify(opts.pagination));
	} else {
		const files = [];
		const clean = data === undefined ? null : stripFiles(data, '', files);
		if (files.length) {
			body = new FormData();
			body.append('data', JSON.stringify(clean));
			if (opts.pagination) body.append('pagination', JSON.stringify(opts.pagination));
			files.forEach((f) => body.append(f.key, f.file, f.file.name || undefined));
		} else {
			headers['Content-Type'] = 'application/json';
			const payload = { data: clean };
			if (opts.pagination) payload.pagination = opts.pagination;
			body = JSON.stringify(payload);
		}
	}

	Object.assign(headers, opts.headers || {});

	let res;
	try {
		res = await window.fetch(target.url, { method, credentials: 'include', headers, body, signal: opts.signal });
	} catch (err) {
		if (err && err.name === 'AbortError') return failure(4, translate('Request aborted'));
		// Network failure, CORS rejection...
		return failure(2, translate('API not responding'));
	}

	// The API always answers with the JSON envelope { success, error?, data?,
	// pagination? } (regardless of the HTTP status), so we hand the body over
	// as-is. External APIs return whatever JSON they produce.
	let text;
	try {
		text = await res.text(); // the abort can also hit mid-body
	} catch (err) {
		return err && err.name === 'AbortError' ? failure(4, translate('Request aborted')) : failure(2, translate('API not responding'));
	}
	try {
		return JSON.parse(text);
	} catch {
		return failure(3, translate('Invalid API response'));
	}
}
